import React, { useState } from 'react';
import { authenticatedFetch } from '../utils/cloudFunctions';
import './AIRefreshIcon.css';

/**
 * 🤖 AI Widget Refresh Icon
 * Erscheint bei Widget-Hover und triggert intelligente Content-Regeneration
 * basierend auf Space-Kontext und aktuellem Widget-Content
 */
function AIRefreshIcon({ 
    widget, 
    spaceId, 
    onContentRefreshed, 
    position = 'top-left' 
}) {
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleAIRefresh = async (e) => {
        e.stopPropagation(); // Prevent widget selection
        
        if (isRefreshing) return;
        
        setIsRefreshing(true);
        
        try {
            console.log('🤖 [AI-REFRESH] Starting content refresh for:', widget.type);
            
            // Extract current user and space from URL
            // URL Pattern: /settings/page/[userId]/[spaceName]/[pageId]
            const currentPath = window.location.pathname;
            const pathParts = currentPath.split('/').filter(part => part); // Remove empty parts
            
            let currentUserId = null;
            let currentSpaceName = null;
            
            // Parse URL: /settings/page/userId/spaceName/pageId
            if (pathParts[0] === 'settings' && pathParts[1] === 'page' && pathParts.length >= 4) {
                currentUserId = pathParts[2];   // nxm4h3KUFAPwLBQQ9aL2j6FK4jr2
                currentSpaceName = pathParts[3]; // schowalter
            }
            
            console.log('🔍 [AI-REFRESH] URL parsing:', {
                currentPath,
                pathParts,
                extractedUserId: currentUserId,
                extractedSpaceName: currentSpaceName
            });
            
            if (!currentUserId || !currentSpaceName) {
                throw new Error(`Missing URL context: userId=${currentUserId}, spaceName=${currentSpaceName}. URL: ${currentPath}`);
            }
            
            console.log('👤 [AI-REFRESH] Using user ID:', currentUserId);
            console.log('🏢 [AI-REFRESH] Using space name:', currentSpaceName);
            
            // Call Cloud Function for widget content refresh (correct project URL)
            const response = await authenticatedFetch(`https://us-central1-bonusclub-ab5e2.cloudfunctions.net/refreshWidgetContent?uid=${currentUserId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    widgetData: widget,
                    spaceId: currentSpaceName, // Send spaceName as spaceId
                    widgetType: widget.type,
                    currentContent: extractCurrentContent(widget)
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success && result.refreshedContent) {
                console.log('✅ [AI-REFRESH] Content refreshed successfully');
                
                // Merge refreshed content with existing widget data
                const updatedWidget = {
                    ...widget,
                    ...result.refreshedContent,
                    lastAIRefresh: new Date().toISOString()
                };
                
                // Callback to parent component
                onContentRefreshed(updatedWidget);
                
                // Show success feedback
                showSuccessToast();
            } else {
                throw new Error(result.error || 'Failed to refresh content');
            }
            
        } catch (error) {
            console.error('❌ [AI-REFRESH] Error refreshing widget content:', error);
            showErrorToast(error.message);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Extract current content for context - ERWEITERTE VERSION für komplexe Strukturen
    const extractCurrentContent = (widget) => {
        // 🚀 NEUE STRATEGIE: Sende ALLE Widget-Daten, Backend macht die intelligente Extraktion
        // Das ist effizienter und erfasst ALLE möglichen Text-Felder automatisch
        
        console.log('🔍 [AI-REFRESH] Extracting content from widget:', widget.type, 'Keys:', Object.keys(widget));
        
        // Erstelle eine bereinigte Version ohne React-spezifische Felder
        const cleanedWidget = {};
        
        // 🚫 BLACKLIST: Design- und System-Felder die NIEMALS an AI gesendet werden
        const excludedFields = [
            // System-Felder
            'id', 'type', 'lastAIRefresh', 'isSelected', 'triggerAnimation',
            
            // Farben (alle Varianten)
            'color', 'textColor', 'backgroundColor', 'borderColor', 'shadowColor',
            'primaryColor', 'secondaryColor', 'accentColor', 'overlayColor',
            'buttonColor', 'buttonTextColor', 'linkColor', 'hoverColor',
            
            // Layout & Styling
            'layout', 'layoutType', 'spacing', 'padding', 'margin', 'borderRadius',
            'fontSize', 'fontFamily', 'fontWeight', 'lineHeight', 'textAlign',
            'width', 'height', 'maxWidth', 'minHeight', 'opacity', 'zIndex',
            
            // Images & Media
            'image', 'backgroundImage', 'profileImage', 'serviceImage', 'beforeImage', 'afterImage',
            'imageUrl', 'imageAlt', 'imageFit', 'imagePosition', 'imageFilter',
            
            // Technical Flags
            'visible', 'enabled', 'showTitle', 'showSubtitle', 'showDescription', 'showButton',
            
            // Animations & Effects
            'animation', 'effect', 'transition', 'transform', 'filter', 'blur',
            'shadow', 'gradient', 'blend', 'rotate', 'scale',
            
            // Icons & Custom Elements
            'icon', 'customIcon', 'logo', 'avatar', 'badge', 'symbol'
        ];

        for (const [key, value] of Object.entries(widget)) {
            const lowerKey = key.toLowerCase();
            
            // Überspringe System-Felder
            if (key.startsWith('_')) {
                continue;
            }
            
            // Überspringe Design-Felder (exakte Matches und Pattern)
            if (excludedFields.some(excluded => 
                lowerKey === excluded.toLowerCase() ||
                lowerKey.includes(excluded.toLowerCase()) ||
                lowerKey.endsWith('color') ||
                lowerKey.endsWith('size') ||
                lowerKey.endsWith('width') ||
                lowerKey.endsWith('height') ||
                lowerKey.startsWith('is') ||
                lowerKey.startsWith('has') ||
                lowerKey.startsWith('show')
            )) {
                continue;
            }
            
            // Nur Text-relevante Felder mitnehmen
            cleanedWidget[key] = value;
        }
        
        console.log('🧹 [AI-REFRESH] Cleaned widget data keys:', Object.keys(cleanedWidget));
        console.log('📦 [AI-REFRESH] Sample data:', {
            hasServices: !!cleanedWidget.services,
            servicesLength: cleanedWidget.services?.length,
            hasQaItems: !!cleanedWidget.qaItems,
            qaItemsLength: cleanedWidget.qaItems?.length,
            hasBulletPoints: !!cleanedWidget.bulletPoints,
            bulletPointsLength: cleanedWidget.bulletPoints?.length,
            textFields: Object.keys(cleanedWidget).filter(key => 
                typeof cleanedWidget[key] === 'string' && cleanedWidget[key].trim()
            )
        });
        
        return cleanedWidget;
    };

    const showSuccessToast = () => {
        // Simple success feedback
        const toast = document.createElement('div');
        toast.className = 'ai-refresh-toast success';
        toast.innerHTML = '✅ Content refreshed with AI!';
        document.body.appendChild(toast);
        
        setTimeout(() => {
            toast.classList.add('fade-out');
            setTimeout(() => document.body.removeChild(toast), 300);
        }, 2000);
    };

    const showErrorToast = (message) => {
        const toast = document.createElement('div');
        toast.className = 'ai-refresh-toast error';
        toast.innerHTML = `❌ AI refresh failed: ${message}`;
        document.body.appendChild(toast);
        
        setTimeout(() => {
            toast.classList.add('fade-out');
            setTimeout(() => document.body.removeChild(toast), 300);
        }, 3000);
    };

    return (
        <div 
            className={`ai-refresh-icon ${position} ${isRefreshing ? 'refreshing' : ''}`}
            onClick={handleAIRefresh}
            title="Refresh content with AI"
        >
            {isRefreshing ? (
                <div className="ai-refresh-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span className="ai-refresh-text">Generating...</span>
                </div>
            ) : (
                <div className="ai-refresh-button">
                    <span className="ai-refresh-text">TEXTGEN AI</span>
                </div>
            )}
        </div>
    );
}

export default AIRefreshIcon;
